<script lang="ts" setup>
    import type { UIChatMessage } from 'models'
    import { ChatTransport } from 'models'
    import { computed } from 'vue'
    import { fromAnyCase, toKebabCase, capitalizeFirstLetter } from 'utils'
    import { useTime } from 'composables/src/useTime'
    import {
        isToolPart,
        getPartIcon,
        isTextPart,
        isLoadingState,
        hasPartText,
    } from './utils'
    import PkCode from './PkCode.vue'
    import PkRelativeTime from '../PkRelativeTime.vue'
    import PkLanguageModel from './PkLanguageModel.vue'
    import PkStreamingMarkdownAutoscroll from './PkStreamingMarkdownAutoscroll.vue'
    import PkToolSelection from './PkToolSelection.vue'
    import PkTurnEvaluation from './PkTurnEvaluation.vue'

    interface SubAgentSlotPart {
        // generic worker call input (no configured sub-agent)
        input?: {
            objective?: string
        }
        output?: {
            subAgentId?: string
            subAgentName?: string
            reasoningChatId?: string
        }
    }

    const props = defineProps<{
        message: UIChatMessage
    }>()

    // readDocumentFile used to stream its file content as base64 in
    // `output.data` and it was truncated here for display; the tool now
    // returns a small reference, so parts render as-is.
    const messageParts = computed(() => {
        return (
            props.message.parts?.filter((item) => item.type !== 'step-start') ||
            []
        )
    })
    const getMessagePartTiming = (toolCallId: string) => {
        const toReturn = props.message?.metadata?.partTimings?.[toolCallId]
        if (toReturn && toReturn > 1000) {
            return toReturn
        }
        return undefined
    }
    const { formatDeltaToString } = useTime()

    const condensedDeviceContext = computed(() => {
        const ctx = props.message?.metadata?.deviceContext
        if (!ctx) {
            return undefined
        }
        const {
            screenWidth,
            screenHeight,
            viewportWidth,
            viewportHeight,
            devicePixelRatio,
            hardwareConcurrency,
            deviceMemory,
            language,
            languages,
            pathname,
            connectionType,
            connectionEffectiveType,
            connectionDownlink,
            connectionRtt,
            darkMode,
            reducedMotion,
            saveData,
            ...rest
        } = ctx as Record<string, unknown>

        const connectionParts = [
            !connectionType || connectionType === 'unknown'
                ? connectionEffectiveType
                : connectionType,
            connectionDownlink !== undefined
                ? `Downlink ${connectionDownlink}Mbps`
                : undefined,
            connectionRtt !== undefined ? `RTT ${connectionRtt}ms` : undefined,
        ].filter(Boolean)

        const flags = [
            darkMode ? 'dark' : undefined,
            reducedMotion ? 'reduced-motion' : undefined,
            saveData ? 'save-data' : undefined,
        ].filter(Boolean)

        return {
            ...rest,
            ...(languages
                ? { language: (languages as string[]).join(', ') }
                : language
                  ? { language }
                  : {}),
            ...(screenWidth !== undefined && screenHeight !== undefined
                ? {
                      screen: `${screenWidth}x${screenHeight}${devicePixelRatio ? ` @${devicePixelRatio}x` : ''}`,
                  }
                : {}),
            ...(viewportWidth !== undefined && viewportHeight !== undefined
                ? { viewport: `${viewportWidth}x${viewportHeight}` }
                : {}),
            ...(hardwareConcurrency !== undefined || deviceMemory !== undefined
                ? {
                      hardware: [
                          hardwareConcurrency !== undefined
                              ? `${hardwareConcurrency} cores`
                              : undefined,
                          deviceMemory !== undefined
                              ? `${deviceMemory}GB`
                              : undefined,
                      ]
                          .filter(Boolean)
                          .join(' · '),
                  }
                : {}),
            ...(connectionParts.length > 0
                ? { connection: connectionParts.join(' · ') }
                : {}),
            ...(flags.length > 0 ? { flags: flags.join(' · ') } : {}),
        }
    })

    /** The message travelled through a call, in either direction. */
    const isSpokenExchange = computed(
        () => props.message?.metadata?.transport === ChatTransport.Voice,
    )

    const hasExternalContext = computed(() => {
        return (
            props.message?.metadata?.externalContext &&
            Object.keys(props.message.metadata.externalContext).length > 0
        )
    })

    const hasPlatformContext = computed(() => {
        return (
            props.message?.metadata?.platformContext &&
            Object.keys(props.message.metadata.platformContext).length > 0
        )
    })
</script>

<template>
    <div class="flex flex-col gap-16">
        <div
            v-if="message.metadata?.model || isSpokenExchange"
            class="grid grid-cols-2 gap-x-10 text-12 text-word-3 justify-between">
            <div class="flex flex-col gap-y-4">
                <PkLanguageModel
                    v-if="message.metadata?.model"
                    :model-name="message.metadata?.model"
                    historical />
                <span
                    v-if="message?.metadata?.resolvedProvider"
                    class="flex items-center gap-4 min-w-0">
                    <VvIcon name="ri:cloud-line" class="shrink-0" />
                    <span class="truncate">
                        {{ message.metadata.resolvedProvider }}
                        <template v-if="message.metadata.resolvedModelId">
                            ({{ message.metadata.resolvedModelId }})
                        </template>
                    </span>
                </span>
                <span
                    v-if="message?.metadata?.totalTokens"
                    class="flex items-center gap-4 min-w-0">
                    <VvIcon name="ri:ai-generate-2-line" class="shrink-0" />
                    <span class="truncate">
                        {{ $n(message.metadata.totalTokens, 'integer') }}
                        {{ $t('label.tokens') }}
                        <span v-if="message?.metadata?.cachedTokens">
                            ({{ $n(message.metadata.cachedTokens, 'integer') }}
                            {{ $t('label.cached') }})
                        </span>
                    </span>
                </span>
                <span
                    v-if="message?.metadata?.zeroDataRetention"
                    class="flex items-center gap-4">
                    <VvIcon name="ri:shield-check-line" class="shrink-0" />
                    {{ $t('label.noDataRetention') }}
                </span>
                <span
                    v-else-if="message?.metadata?.disallowPromptTraining"
                    class="flex items-center gap-4">
                    <VvIcon name="ri:shield-check-line" class="shrink-0" />
                    {{ $t('label.noPromptTraining') }}
                </span>
            </div>
            <div class="flex flex-col gap-y-4 items-end">
                <time
                    v-if="message?.metadata?.createdAt"
                    :datetime="
                        new Date(message.metadata.createdAt).toISOString()
                    "
                    class="flex items-center gap-4">
                    <VvIcon name="ri:time-line" class="shrink-0" />
                    {{ $d(new Date(message.metadata.createdAt), 'date-time') }}
                </time>
                <div
                    v-if="
                        message?.metadata?.completedAt &&
                        message?.metadata?.createdAt
                    "
                    class="flex items-center gap-4">
                    <VvIcon name="ri:hourglass-line" class="shrink-0" />
                    <PkRelativeTime
                        :date="message.metadata?.createdAt"
                        :end-date="message.metadata?.completedAt" />
                    <span
                        v-if="
                            message?.metadata?.totalProviderAttemptCount &&
                            message.metadata.totalProviderAttemptCount > 1
                        ">
                        ({{ message.metadata.totalProviderAttemptCount }}
                        attempts)
                    </span>
                </div>
                <span
                    v-if="
                        message?.metadata?.cost &&
                        parseFloat(message.metadata.cost) > 0
                    "
                    class="flex items-center gap-4">
                    <VvIcon name="ri:wallet-line" class="shrink-0" />
                    {{ $n(parseFloat(message.metadata.cost), 'currencyUSD') }}
                </span>
                <!-- Spoken exchange: the user text is the call transcription,
                     the assistant text was read aloud. Up here with the model
                     and the cost because it is the same kind of fact about the
                     message, and a message can have twenty parts under it. -->
                <span
                    v-if="isSpokenExchange"
                    class="flex items-center gap-4 min-w-0">
                    <VvIcon name="ri:mic-line" class="shrink-0" />
                    <span class="truncate">{{
                        $t('label.spokenExchange')
                    }}</span>
                </span>
            </div>
        </div>
        <VvAccordionGroup
            v-if="
                condensedDeviceContext ||
                hasPlatformContext ||
                hasExternalContext
            "
            class="gap-6">
            <VvAccordion
                v-if="hasPlatformContext"
                modifiers="details"
                :title="$t('label.platformContext')"
                class="border border-surface-3 rounded">
                <dl
                    class="grid grid-cols-3 gap-x-8 gap-y-2 text-10 text-word-3">
                    <template
                        v-for="(value, key) in message.metadata
                            ?.platformContext"
                        :key="key">
                        <dt class="font-semibold truncate">
                            {{ capitalizeFirstLetter(fromAnyCase(key)) }}
                        </dt>
                        <dd
                            class="truncate font-mono col-span-2"
                            :title="String(value)">
                            {{ value }}
                        </dd>
                    </template>
                </dl>
            </VvAccordion>
            <VvAccordion
                v-if="condensedDeviceContext"
                modifiers="details"
                :title="$t('label.deviceContext')"
                class="border border-surface-3 rounded">
                <dl
                    class="grid grid-cols-3 gap-x-8 gap-y-2 text-10 text-word-3">
                    <template
                        v-for="(value, key) in condensedDeviceContext"
                        :key="key">
                        <dt class="font-semibold truncate">
                            {{ capitalizeFirstLetter(fromAnyCase(key)) }}
                        </dt>
                        <dd
                            class="truncate font-mono col-span-2"
                            :title="String(value)">
                            {{ value }}
                        </dd>
                    </template>
                </dl>
            </VvAccordion>
            <VvAccordion
                v-if="hasExternalContext"
                modifiers="details"
                :title="$t('label.externalContext')"
                class="border border-surface-3 rounded">
                <dl
                    class="grid grid-cols-3 gap-x-8 gap-y-2 text-10 text-word-3">
                    <template
                        v-for="(value, key) in message.metadata
                            ?.externalContext"
                        :key="key">
                        <dt class="font-semibold truncate">
                            {{ capitalizeFirstLetter(fromAnyCase(key)) }}
                        </dt>
                        <dd
                            class="truncate font-mono col-span-2"
                            :title="String(value)">
                            {{ value }}
                        </dd>
                    </template>
                </dl>
            </VvAccordion>
        </VvAccordionGroup>
        <!-- Before the steps: the tool selection is what ran before the first. -->
        <PkToolSelection :message="message" />
        <ol class="flex flex-col gap-16">
            <li v-for="(part, index) in messageParts" :key="index">
                <span
                    class="text-12 text-word-2 leading-none flex items-center gap-12 bg-surface-1 rounded-t border-t border-x border-surface-3 p-8"
                    :class="{
                        'border-b rounded-b':
                            !isToolPart(part) && !hasPartText(part),
                    }">
                    <div
                        class="flex items-center bg-gray-lighten-1 text-white px-6 py-3 rounded">
                        <span
                            class="tracking-widest border-r border-gray-lighten-3 pr-4"
                            ><strong class="font-bold text-12">{{
                                index + 1
                            }}</strong
                            ><span class="text-10"
                                >/{{ messageParts.length }}</span
                            >
                        </span>
                        <template
                            v-if="
                                (isToolPart(part) || isTextPart(part)) &&
                                isLoadingState(part.state)
                            ">
                            <VvIcon
                                name="loading-loop"
                                class="w-12 h-12 ml-4" />
                        </template>
                        <VvIcon
                            v-else
                            :name="getPartIcon(part) || 'ri:question-line'"
                            class="w-12 h-12 ml-4" />
                    </div>
                    <code class="font-mono text-10 text-word-2 truncate">
                        {{ toKebabCase(part.type) }}
                    </code>
                    <span
                        v-if="
                            'toolCallId' in part &&
                            getMessagePartTiming(part.toolCallId)
                        "
                        class="text-10 text-word-4 whitespace-nowrap ml-auto">
                        {{
                            formatDeltaToString(
                                getMessagePartTiming(part.toolCallId),
                            )
                        }}
                    </span>
                </span>
                <div v-if="isToolPart(part)">
                    <div class="border rounded-b border-surface-3">
                        <div
                            class="font-bold text-10 uppercase p-8 border-b border-surface-3">
                            {{ $t('label.input') }}
                        </div>
                        <PjResizablePanel :min-height="128" fit-content>
                            <PkCode
                                :code="part.input ?? 'null'"
                                language="json"
                                class="text-12 overflow-hidden font-mono leading-snug bg-surface-1" />
                        </PjResizablePanel>
                        <template v-if="part.output && part.output !== 'null'">
                            <div
                                class="font-bold text-10 uppercase p-8 border-b border-t border-surface-3">
                                {{ $t('label.output') }}
                            </div>
                            <PjResizablePanel :min-height="128" fit-content>
                                <PkCode
                                    :code="part.output"
                                    language="json"
                                    class="text-12 overflow-hidden font-mono leading-snug bg-surface-1" />
                            </PjResizablePanel>
                        </template>
                        <template
                            v-if="
                                part.type.includes('tool-subagent') ||
                                part.type === 'tool-runWorker'
                            ">
                            <slot
                                name="subagent"
                                :part="part as SubAgentSlotPart" />
                        </template>
                        <slot
                            v-else
                            :name="toKebabCase(part.type)"
                            :part="part" />
                    </div>
                </div>
                <PjResizablePanel
                    v-if="hasPartText(part)"
                    :min-height="128"
                    fit-content
                    class="border border-surface-3 rounded-b p-xs leading-normal bg-surface text-word-2">
                    <slot name="text" :part="part">
                        <PkStreamingMarkdownAutoscroll
                            :markdown="part.text"
                            inner-class="wysiwyg text-14" />
                    </slot>
                </PjResizablePanel>
            </li>
        </ol>
        <!-- After the steps: the evaluation runs once the reply is written. -->
        <PkTurnEvaluation :message="message" />
    </div>
</template>
